// ABOUTME: Unified header bar for the child dashboard
// ABOUTME: Displays back arrow + avatar/name, teeter totter, and behavior count squares in a 3-column responsive layout

import React from 'react';
import { Box, IconButton, Paper, Typography, Avatar } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import ChildProfileHeader from './ChildProfileHeader';
import PositivityRatioBalance from './PositivityRatioBalance';
import TodaysBehaviorCount from './TodaysBehaviorCount';
import naluV1Theme from '../theme/theme.nalu-v1';
import { getTodayInFamilyTimezone } from '../utils/dateUtils';

export interface BehaviorCounts {
  stars: number;
  triangles: number;
  squares: number;
  currentStage?: number;
  currentPositionDisplayName?: string;
  allPositionDisplayNames?: string[];
  flipYourCoins?: number;
  oneOnOneTimes?: number;
  makeItRights?: number;
}

export interface ChildDashboardHeaderProps {
  childName: string;
  profileImageUrl?: string | null;
  behaviorCounts: BehaviorCounts;
  loading: boolean;
  showBackButton?: boolean;
  familyTimezone?: string;
}

export const ChildDashboardHeader: React.FC<ChildDashboardHeaderProps> = ({
  childName,
  profileImageUrl,
  behaviorCounts,
  loading,
  showBackButton = true,
  familyTimezone,
}) => {
  const theme = useTheme();
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;

  // nalu-v1 cards rely on the theme's global MuiPaper override (flat #F8F1E4 fill,
  // 1px hairline border, no shadow) — a bare Paper matches the mockup on its own,
  // so no bgcolor/boxShadow/borderRadius overrides are needed here (issue #1900).
  const columnSx = isNaluV1Theme ? {
    flex: { xs: '1 1 auto', md: '1 1 33%' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: { xs: '18px', md: '22px' },
    minHeight: { xs: 'auto', md: 100 },
  } : {
    flex: { xs: '1 1 auto', md: '1 1 33%' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: { xs: 1, md: 1.5 },
    borderRadius: 2,
    bgcolor: 'background.paper',
    boxShadow: 1,
    minHeight: { xs: 'auto', md: 100 },
  };

  const dateLabel = (() => {
    const todayStr = getTodayInFamilyTimezone(familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [year, month, day] = todayStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  })();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 1, md: 2 },
        mb: { xs: 2, md: 3 },
        alignItems: { md: 'stretch' },
      }}
    >
      {/* Left column: Back arrow + avatar + name */}
      <Paper
        elevation={1}
        sx={{
          ...columnSx,
          justifyContent: 'flex-start',
          gap: 1,
        }}
      >
        {showBackButton && (
          <IconButton
            onClick={() => window.history.back()}
            sx={isNaluV1Theme ? {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
            } : {
              bgcolor: 'grey.100',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            aria-label="Go back"
          >
            <BackIcon />
          </IconButton>
        )}
        {isNaluV1Theme ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={profileImageUrl || undefined}
              alt={`${childName}'s profile picture`}
              sx={{ width: 64, height: 64, bgcolor: 'secondary.main', fontWeight: 800, fontSize: '26px' }}
            >
              {childName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography sx={{ fontFamily: theme.typography.h6.fontFamily, fontSize: '28px', fontWeight: 600, lineHeight: 1.1 }}>
                Aloha, {childName}!
              </Typography>
              <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: '4px' }}>
                {dateLabel} &middot; your voyage today
              </Typography>
            </Box>
          </Box>
        ) : (
          <ChildProfileHeader
            childName={childName}
            profileImageUrl={profileImageUrl}
            size="medium"
          />
        )}
      </Paper>

      {/* Center column: Positivity ratio balance (teeter totter) */}
      <Paper elevation={1} sx={columnSx}>
        <PositivityRatioBalance
          behaviorCounts={behaviorCounts}
          loading={loading}
          compact
        />
      </Paper>

      {/* Right column: Today's behavior counts */}
      <Paper elevation={1} sx={columnSx}>
        <TodaysBehaviorCount
          behaviorCounts={behaviorCounts}
          loading={loading}
          compact
        />
      </Paper>
    </Box>
  );
};
